import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import {
  hiddenNumberFields,
  Psa,
  PsaMetadata,
  StorePsaSchema,
  UpdatePsaSchema,
} from '../../shared/models/psa.model';
import { PsaService } from '../../shared/services/psa.service';
import { applyZodErrors } from '../../shared/util/zod-form-errors';

/** The three manufacturer-number controls, in the order the dialog shows them. */
const NUMBER_FIELDS = [
  { key: 'manufacturer_number_set', label: 'psa.manufacturerNumberSet' },
  { key: 'manufacturer_number_harness', label: 'psa.manufacturerNumberHarness' },
  {
    key: 'manufacturer_number_carabiners',
    label: 'psa.manufacturerNumberCarabiners',
  },
] as const;

type NumberField = (typeof NUMBER_FIELDS)[number]['key'];

@Component({
  selector: 'app-psa-list-page',
  imports: [
    CommonModule,
    DatePipe,
    BadgeModule,
    CardModule,
    DatePickerModule,
    TableModule,
    TranslateModule,
    ButtonModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MenuModule,
    MultiSelectModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    DialogModule,
    ReactiveFormsModule,
  ],
  providers: [MessageService, PsaService, ConfirmationService],
  templateUrl: './psa-list-page.component.html',
  styleUrl: './psa-list-page.component.scss',
})
export class PsaListPageComponent implements OnInit {
  #router = inject(Router);
  #psaService = inject(PsaService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #formBuilder = inject(FormBuilder);

  private psaTableRef = viewChild<Table>('psaTable');

  public readonly numberFields = NUMBER_FIELDS;

  public psaColumns = PsaMetadata.psaColumns;
  public globalFilterFields = PsaMetadata.globalFilterFields;
  /** Toggler column + the action column bracket the metadata-driven columns. */
  public detailColspan = PsaMetadata.psaColumns.length + 2;

  psas = this.#psaService.psas;

  filterOptions = computed(() => {
    this.psas(); // signal dependency: re-evaluate after data loads
    const t = (key: string) => this.#translateService.instant(key);
    return {
      type: [
        { label: t('psa.typeAdult'), value: 'adult' },
        { label: t('psa.typeChild'), value: 'child' },
      ],
    } as Record<string, { label: string; value: string }[]>;
  });

  createMenuItems: MenuItem[] = [
    { label: '', command: () => this.openCreateDialog('adult') },
    { label: '', command: () => this.openCreateDialog('child') },
  ];

  // ── Row expansion ────────────────────────────────────────────────────

  searchTerm = signal('');
  /** Rows the user toggled by hand, overriding the search-derived state. */
  #rowOverrides = signal<Record<string, boolean>>({});

  /**
   * Expand only rows whose match is invisible while collapsed — a hit on the
   * id, the type or the set number is already on screen, and expanding those
   * would open every row on a search like "MRG".
   *
   * Derived rather than assigned so a list refresh after a save re-runs it.
   */
  #searchExpanded = computed<Record<string, boolean>>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (term === '') {
      return {};
    }

    return this.psas().reduce<Record<string, boolean>>((keys, psa) => {
      const hit = hiddenNumberFields.some((field) =>
        (psa[field] ?? '').toLowerCase().includes(term),
      );
      if (hit) {
        keys[psa.id] = true;
      }
      return keys;
    }, {});
  });

  /**
   * Expansion state is owned here, not by the table. PrimeNG mutates the object
   * bound to `expandedRowKeys` in place and emits no change event, so it must
   * never be handed a object it can write through — this computed produces a
   * fresh one, which is also what repaints it under zoneless change detection.
   */
  expandedRowKeys = computed<Record<string, boolean>>(() => {
    const merged = { ...this.#searchExpanded() };
    for (const [id, open] of Object.entries(this.#rowOverrides())) {
      if (open) {
        merged[id] = true;
      } else {
        delete merged[id];
      }
    }
    return merged;
  });

  toggleRow(psa: Psa) {
    const open = !this.expandedRowKeys()[psa.id];
    this.#rowOverrides.update((overrides) => ({
      ...overrides,
      [psa.id]: open,
    }));
  }

  isExpanded(psa: Psa): boolean {
    return !!this.expandedRowKeys()[psa.id];
  }

  onGlobalSearch(value: string) {
    this.psaTableRef()?.filterGlobal(value, 'contains');
    this.searchTerm.set(value);
    // A new search supersedes whatever the user had toggled by hand.
    this.#rowOverrides.set({});
  }

  // ── Create / edit dialog ─────────────────────────────────────────────

  dialogVisible = signal(false);
  /** null in create mode; the PSA being edited otherwise. */
  editingPsa = signal<Psa | null>(null);
  /** Type chosen from the add menu; fixed for the lifetime of a create dialog. */
  pendingType = signal<'adult' | 'child' | null>(null);
  saving = signal(false);

  numberForm: FormGroup = this.#formBuilder.group({
    manufacturer_number_set: [null as string | null],
    manufacturer_number_harness: [null as string | null],
    manufacturer_number_carabiners: [null as string | null],
  });

  /** `type` is display-only in both modes — it is fixed at creation (R7). */
  dialogType = computed(() => this.editingPsa()?.type ?? this.pendingType());

  openCreateDialog(type: 'adult' | 'child') {
    this.numberForm.reset();
    this.editingPsa.set(null);
    this.pendingType.set(type);
    this.dialogVisible.set(true);
  }

  openEditDialog(psa: Psa) {
    this.numberForm.reset();
    this.numberForm.patchValue({
      manufacturer_number_set: psa.manufacturer_number_set,
      manufacturer_number_harness: psa.manufacturer_number_harness,
      manufacturer_number_carabiners: psa.manufacturer_number_carabiners,
    });
    this.pendingType.set(null);
    this.editingPsa.set(psa);
    this.dialogVisible.set(true);
  }

  closeDialog() {
    this.dialogVisible.set(false);
    // The dialog is gone, so its in-flight state is meaningless. Without this a
    // dismissed save leaves saving() true and disables the next dialog's Save
    // button until the abandoned request happens to land.
    this.saving.set(false);
    this.editingPsa.set(null);
    this.pendingType.set(null);
    this.numberForm.reset();
  }

  isInvalid(field: NumberField): boolean {
    const control = this.numberForm.get(field);
    return !!control && control.invalid && control.touched;
  }

  errorKey(field: NumberField): string {
    return this.numberForm.get(field)?.errors?.['zod']?.message ?? '';
  }

  /**
   * Numbers are copied off physical equipment by hand, so trailing whitespace is
   * routine. An entry that is empty after trimming is a cleared field, and the
   * endpoint distinguishes "cleared" from "not sent" — so send null, don't omit.
   */
  #normalizedNumbers(): Record<NumberField, string | null> {
    const raw = this.numberForm.value as Record<NumberField, string | null>;
    return NUMBER_FIELDS.reduce(
      (acc, { key }) => {
        const trimmed = (raw[key] ?? '').trim();
        acc[key] = trimmed === '' ? null : trimmed;
        return acc;
      },
      {} as Record<NumberField, string | null>,
    );
  }

  saveDialog() {
    const numbers = this.#normalizedNumbers();
    const psa = this.editingPsa();

    const result = psa
      ? UpdatePsaSchema.safeParse(numbers)
      : StorePsaSchema.safeParse({ type: this.pendingType(), ...numbers });

    if (!result.success) {
      applyZodErrors(result.error, this.numberForm);
      this.#messageService.add({
        severity: 'error',
        summary: this.#translateService.instant('label.errorHeader'),
        detail: this.#translateService.instant('psa.saveError'),
      });
      return;
    }

    this.saving.set(true);

    // Captured before the request goes out: the dialog can be dismissed while
    // it is in flight, and closeDialog() clears pendingType.
    const isCreate = !psa;
    const createdType = this.pendingType();

    const request = psa
      ? this.#psaService.updatePsa(psa.id, numbers)
      : this.#psaService.createPsa({
          type: createdType as 'adult' | 'child',
          ...numbers,
        });

    request.subscribe({
      next: () => {
        this.#psaService.getPsas();

        if (isCreate) {
          // Keep the existing "find the row you just created" behaviour.
          const table = this.psaTableRef();
          if (table) {
            table.filter([createdType], 'type', 'in');
            table.sort({ field: 'id', order: 1 });
          }
        }

        // Also clears saving().
        this.closeDialog();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translateService.instant('label.successHeader'),
          detail: this.#translateService.instant(
            isCreate ? 'psa.created' : 'psa.saved',
          ),
        });
      },
      error: () => {
        // Leave the dialog open: the numbers were typed off the physical
        // equipment and closing would throw the entry away.
        this.saving.set(false);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translateService.instant('label.errorHeader'),
          detail: this.#translateService.instant(
            isCreate ? 'psa.createError' : 'psa.saveError',
          ),
        });
      },
    });
  }

  updateCreateMenuLabels() {
    this.createMenuItems[0].label =
      this.#translateService.instant('psa.typeAdult');
    this.createMenuItems[1].label =
      this.#translateService.instant('psa.typeChild');
  }

  ngOnInit(): void {
    this.#psaService.getPsas();
  }

  navigateToCreateDefect(psa: Psa) {
    this.#router.navigate(['/defects/new'], {
      queryParams: {
        modelType: 'psa',
        modelId: psa.id,
        returnUrl: '/psas',
      },
    });
  }

  navigateToDefects(psa: Psa) {
    this.#router.navigate(['/defects'], {
      queryParams: { modelName: psa.id },
    });
  }

  navigateToInspections(psa: Psa) {
    this.#router.navigate(['/inspections'], {
      queryParams: { modelType: 'psa', modelId: psa.id, modelName: psa.id },
    });
  }

  navigateToOpenDefects(psa: Psa) {
    this.#router.navigate(['/defects'], {
      queryParams: { modelName: psa.id, status: 'open' },
    });
  }

  onRowDeleteInit(event: Event, psa: Psa) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant('psa.deleteConfirmMessage'),
      header: this.#translateService.instant('psa.deleteHeader'),
      rejectLabel: this.#translateService.instant('label.cancel'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.#psaService.deletePsa(psa.id).subscribe({
          next: () => {
            this.#psaService.getPsas();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
      reject: () => {
        //nothing
      },
    });
  }
}
